import { LitElement, css, html } from 'lit';
import { msg, localized } from '@lit/localize';
import { customElement, property } from 'lit/decorators.js';
import { localesMap, matchLocalesMap } from '../constant.js'
import { globalStyles, skeletonStyles } from '../global-styles.js'
import { IMerchantCustomizeConfig } from '../types/customSettings.js';

@localized()
@customElement('pp-cashier-header')
export class PpCashierHeader extends LitElement {
  static styles = [
    globalStyles,
    skeletonStyles,
    css`
      :host {
        display: block;
        width: 100%;
        color: var(--pp-checkout-header-text-color);
        background-color: var(--pp-checkout-header-bg-color, #FFFFFF);
      }

      header {
        margin: 0 auto;
        height: 3rem;
        line-height: 3rem;
        max-width: 36rem;
        position: relative;
        justify-content: var(--pp-checkout-logo-position, left);
      }

      .pp-secure h1 {
        max-width: 320px;
        line-height: 20px;
      }
      
      #lang-select {
        outline: none;
        text-align: right;
        padding-right: 0.625rem;
        background-color: transparent;
      }

      .pp-merchant__logo {
        height: 1.5rem;
      }

      .pp-merchant__logo-line {
        width: 1px;
        height: 1.25rem;
        background-color: var(--pp-checkout-header-text-color);
        margin: 0 8px;
      }
      
      .pp-switch-locales {
        position: absolute;
        right: 0;
      }

      .LOADING-payment-header {
        height: 48px;
        margin-bottom: 0;
        border-radius: 0;
      }

      @media only screen and (min-width: 0) and (max-width: 576px){
        :host {
          display: none;
        }
        
        .pp-secure h1 {
          max-width: 10.5rem;
        }
      }
    `
  ];

  @property({ type: String }) locale = '';

  @property({ type: String }) width = '36rem';

  @property({ type: Boolean }) fulfilled = false;
  
  @property({ type: Object }) customizeConfig: Partial<IMerchantCustomizeConfig> = {};

  get isZeroPayBinding() {
    return window.PingPong?.Checkout?.zeroPayBinding;
  }

  get headerText() {
    if(this.isZeroPayBinding) {
      return msg('MANAGE PAYMENT METHOD');
    }

    const { headerText } = this.customizeConfig;
    const defaultText = 'SECURE CHECKOUT';
    const text = headerText ?? defaultText;

    return headerText === defaultText? msg('SECURE CHECKOUT'): text;
  }

  get languages () {
    const { languages } = this.customizeConfig;

    return languages ? matchLocalesMap(languages) : localesMap;
  }
  
  private _handleLocaleChange(event: Event) {
    const { value } = event.target as HTMLSelectElement;
    this.dispatchEvent(new CustomEvent('locale-changed', { detail: value }));
  }

  private _renderLocalsOptions() {
    return this.languages.map(locale => html`
      <option 
        value=${locale.value}
        .selected=${this.locale === locale.value}
      >
        ${locale.label}
      </option>
    `)
  }

  // 渲染商户定制logo
  private _renderMerchantLogo() {
    const { cashierLogoUrl, cashierName = '', displayHeaderText = true } = this.customizeConfig;
    if(!cashierLogoUrl) return '';
    return html`
      <img class="pp-merchant__logo" src="${cashierLogoUrl}" alt="${cashierName}"/>
      ${displayHeaderText? html`<i class="pp-merchant__logo-line"></i>`: ''}
    `
  }

  render() {
    const { displayHeaderText = true } = this.customizeConfig;

    if(!this.fulfilled) return html`<div class="LOADING-payment-header LOADING-loads"></div>`
    
    return html`
      <header class="flex items-center" style="max-width: ${this.width}">
        <div class="pp-secure flex items-center">
          ${this._renderMerchantLogo()}
          ${
            displayHeaderText? html`
              <h1 class="text-ellipsis text-base font-bold">
                ${this.headerText}
              </h1>
            `: ''
          }
        </div>
        ${
          this.languages.length > 1 ? html`
            <div class="pp-switch-locales">
              <select id="lang-select" class="text-sm" @change="${this._handleLocaleChange}">
                ${this._renderLocalsOptions()}
              </select>
            </div>
          `: ''
        }
      </header>
    `;
  }
}
